import type { JSX } from 'react';
import { Fragment, useEffect } from 'react';
import { ActionIcon, Alert, Anchor, Button, Group, HoverCard, Loader, Menu, Stack, Tabs, Text, Title } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api, projectApi, snapshotApi, undoPaths } from '../api';
import { EditableText } from '../ui/EditableText';
import { guardedStep, inversePatch, useUndo } from '../ui/undo';
import { useCan } from './useMe';
import { CORE_VIEWS, MODULE_VIEWS } from './moduleViews';
import { ProjectSummary } from './ProjectSummary';
import { linkTo } from '../ui/links';

/**
 * The frame around everything inside one project: where you are, which
 * project it is, and the project's areas as tabs.
 */
export function ProjectLayout(): JSX.Element {
  const { projectId = '' } = useParams();
  const base = `/projekte/${projectId}`;
  const navigate = useNavigate();
  const location = useLocation();

  const projects = useQuery({ queryKey: ['projects'], queryFn: api.projects });
  const canEdit = useCan('projects.write');
  const queryClient = useQueryClient();
  const undo = useUndo();
  const rename = useMutation({
    mutationFn: (body: { number?: string; name?: string }) => {
      const step = project
        ? guardedStep(`Projekt ${project.number}`, 'PATCH', undoPaths.project(projectId), inversePatch(body, { number: project.number, name: project.name }), body)
        : null;
      return undo.recordAfter(projectApi.update(projectId, { ...body, version: project?.version }), step);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });
  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });

  // Opening a project keeps a snapshot of it - the server skips it when nothing changed (ADR 0005).
  useEffect(() => {
    if (!projectId) return;
    snapshotApi.take(projectId, 'open')
      .then((r) => { if (r.created) void queryClient.invalidateQueries({ queryKey: ['snapshots', projectId] }); })
      .catch(() => undefined);
  }, [projectId, queryClient]);

  const project = (projects.data ?? []).find((entry) => entry.id === projectId);
  const active = new Set((modules.data ?? []).filter((m) => m.active).map((m) => m.key));
  const views = [...MODULE_VIEWS.filter((view) => active.has(view.module)), ...CORE_VIEWS];
  // The segment after /projekte/:id - matching by substring would find "projekt" in "projekte".
  const segment = location.pathname.split('/')[3] ?? '';
  const current = views.find((view) => view.path === segment);
  const groupOf = (view: { group?: string }): string | undefined => view.group;

  if (projects.isSuccess && !project) {
    return (
      <Stack>
        <Text>Dieses Projekt gibt es nicht, oder es ist nicht mehr zugänglich.</Text>
        <Anchor {...linkTo(navigate, '/')}>Zur Projektliste</Anchor>
      </Stack>
    );
  }

  return (
    <Stack gap="xs" style={{ flex: 1, minHeight: 0 }}>
      {/* A print page brings its own heading; the frame stays off the paper there. */}
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="xs" className={location.pathname.endsWith('/druck') ? 'pst-no-print' : undefined}>
        {/* The name keeps at least this much room; when space runs out the tabs move to a line of their own. */}
        <Stack gap={0} style={{ minWidth: 0, flex: '1 1 440px' }}>
          <Group gap="xs" align="center" wrap="nowrap">
            <ActionIcon component="a" variant="subtle" color="gray" size="sm" {...linkTo(navigate, '/')} aria-label="Zur Projektliste" title="Zur Projektliste">{'←'}</ActionIcon>
            {project ? (
              canEdit ? (
                <>
                  <div style={{ flexShrink: 0 }}>
                    <EditableText
                      value={project.number}
                      monospace
                      dimmed
                      title="Projektnummer ändern"
                      onCommit={(number) => rename.mutate({ number })}
                    />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <EditableText
                      value={project.name}
                      size="xl"
                      bold
                      title="Projektnamen ändern"
                      onCommit={(name) => rename.mutate({ name })}
                    />
                  </div>
                </>
              ) : (
                <>
                  <Text size="sm" c="dimmed" ff="monospace">{project.number}</Text>
                  <Title order={3} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {project.name}
                  </Title>
                </>
              )
            ) : (
              <Loader size="xs" />
            )}
            {/* Description, client and dates on demand - the frame stays one line high. */}
            {project && (
              <HoverCard width={380} shadow="md" openDelay={150} position="bottom-start">
                <HoverCard.Target>
                  <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Projektangaben" style={{ flexShrink: 0 }}>ⓘ</ActionIcon>
                </HoverCard.Target>
                <HoverCard.Dropdown>
                  <Stack gap={4}>
                    {project.location && <Text size="xs" c="dimmed">Standort: {project.location}</Text>}
                    <ProjectSummary projectId={project.id} lines={8} />
                    <Anchor size="xs" {...linkTo(navigate, `${base}/projekt`)}>Projektdaten öffnen</Anchor>
                  </Stack>
                </HoverCard.Dropdown>
              </HoverCard>
            )}
          </Group>
        </Stack>

        {views.length > 1 && (
          <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
            <Tabs value={current && !current.more ? current.path : null} variant="pills">
              <Tabs.List style={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
                {views.filter((view) => !view.more).map((view, i, shown) => (
                  <Fragment key={view.path}>
                    {/* A thin line where one group of tabs ends and the next begins. */}
                    {i > 0 && groupOf(view) !== groupOf(shown[i - 1]!) && (
                      <div aria-hidden style={{ width: 1, alignSelf: 'stretch', margin: '6px 6px', background: 'var(--mantine-color-default-border)' }} />
                    )}
                    <Tabs.Tab value={view.path} component="a" {...linkTo(navigate, `${base}/${view.path}`)}>{view.label}</Tabs.Tab>
                  </Fragment>
                ))}
              </Tabs.List>
            </Tabs>
            {views.some((view) => view.more) && (
              <div aria-hidden style={{ width: 1, alignSelf: 'stretch', margin: '6px 2px', background: 'var(--mantine-color-default-border)' }} />
            )}
            {views.some((view) => view.more) && (
              <Menu position="bottom-end">
                <Menu.Target>
                  <Button size="sm" radius="md" variant={current?.more ? 'filled' : 'subtle'} color={current?.more ? 'brand' : 'gray'} rightSection={<span aria-hidden>▾</span>}>
                    {current?.more ? current.label : 'Mehr'}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {views.filter((view) => view.more).map((view) => (
                    <Menu.Item key={view.path} component="a" {...linkTo(navigate, `${base}/${view.path}`)} fw={view.path === current?.path ? 600 : undefined}>{view.label}</Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        )}
      </Group>

      {rename.isError && (
        <Alert color="red" withCloseButton onClose={() => rename.reset()}>
          {rename.error instanceof ApiError ? rename.error.message : 'Umbenennen fehlgeschlagen.'}
        </Alert>
      )}

      <Outlet />
    </Stack>
  );
}

/** /projekte/:id on its own lands on the first area the project offers. */
export function ProjectIndex(): JSX.Element {
  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });
  if (!modules.data) return <Loader />;
  const active = new Set(modules.data.filter((m) => m.active).map((m) => m.key));
  const first = MODULE_VIEWS.find((view) => active.has(view.module));
  return first ? <Navigate to={first.path} replace /> : <Text>Für dieses Projekt ist kein Fachmodul aktiv.</Text>;
}
